import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Wallet, AlertTriangle, LogOut, ChevronDown } from "lucide-react";
import { formatUnits } from "viem";
import { useArcWallet } from "@/lib/wallet";

const truncate = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "");

const panelCls = (align) =>
    `absolute ${align === "right" ? "right-0" : "left-0"} top-[calc(100%+10px)] z-50 w-72 max-w-[calc(100vw-48px)] border border-white/10 bg-[#07111F] p-4 text-left shadow-[0_16px_48px_rgba(0,0,0,0.6)]`;

export const WalletButton = ({ align = "left", block = false }) => {
    const {
        address, chainId, isConnected, isWrongNetwork, walletOptions,
        connecting, connectError, connectWallet, switchToArc, disconnectWallet, getUSDCBalance, getACTRABalance,
    } = useArcWallet();
    const [open, setOpen] = useState(false);
    const [balance, setBalance] = useState(null);
    const [actraBalance, setActraBalance] = useState(null);
    const [switchError, setSwitchError] = useState(null);
    const ref = useRef(null);
    const hasWallet = typeof window !== "undefined" && !!window.ethereum;
    const showOptions = hasWallet && walletOptions.length > 0;

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    useEffect(() => {
        let live = true;
        setBalance(null);
        if (isConnected && !isWrongNetwork) {
            getUSDCBalance()
                .then((b) => live && setBalance(formatUnits(b, 6)))
                .catch(() => live && setBalance(null));
            getACTRABalance()
                .then((v) => live && setActraBalance(v))
                .catch(() => live && setActraBalance(null));
        }
        return () => {
            live = false;
        };
    }, [address, chainId, isConnected, isWrongNetwork]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!isConnected) {
        return (
            <div className={`relative ${block ? "w-full" : "w-fit"}`} ref={ref}>
                <button
                    data-testid="wallet-button"
                    aria-expanded={open}
                    onClick={() => setOpen((v) => !v)}
                    className="inline-flex items-center gap-2 whitespace-nowrap border border-white/15 px-3.5 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:border-[#1677FF]"
                >
                    <Wallet size={13} /> {connecting ? "CONNECTING…" : "CONNECT WALLET"}
                </button>
                <AnimatePresence>
                    {open && (
                        <motion.div
                            data-testid="wallet-dropdown"
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 6 }}
                            transition={{ duration: 0.25 }}
                            className={panelCls(align)}
                        >
                            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-[#31D8FF]">
                                SELECT WALLET — ARC MAINNET (5042)
                            </p>
                            <div className="mt-3 space-y-2">
                                {showOptions ? (
                                    walletOptions.map((c) => (
                                        <button
                                            key={c.uid}
                                            data-testid={`wallet-option-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                            onClick={() => {
                                                setSwitchError(null);
                                                connectWallet(c);
                                            }}
                                            className="flex w-full items-center justify-between border border-white/10 bg-[#0A1627] px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:border-[#1677FF]"
                                        >
                                            {c.name} <span className="text-mute">INJECTED</span>
                                        </button>
                                    ))
                                ) : (
                                    <p data-testid="wallet-no-provider" className="border border-dashed border-white/15 px-4 py-4 text-center text-xs leading-relaxed text-mute">
                                        NO WALLET DETECTED IN THIS BROWSER.
                                        <br />
                                        INSTALL METAMASK, RABBY OR OKX TO CONNECT.
                                    </p>
                                )}
                            </div>
                            {connectError && (
                                <p data-testid="wallet-error" className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#FF5C7A]">
                                    <AlertTriangle size={11} /> {connectError.message}
                                </p>
                            )}
                            <p className="mt-3 text-[11px] leading-relaxed text-mute/70">
                                MetaMask, Rabby, OKX and other injected EVM wallets. WalletConnect remote pairing follows with a
                                project ID.
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    }

    return (
        <div className="relative w-fit" ref={ref}>
            <button
                data-testid="wallet-button-connected"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-2 whitespace-nowrap border border-[#1677FF]/60 bg-[#1677FF]/10 px-3.5 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:border-[#1677FF]"
            >
                {isWrongNetwork ? (
                    <span className="flex items-center gap-1.5 text-[#FF5C7A]">
                        <AlertTriangle size={12} /> WRONG NETWORK
                    </span>
                ) : (
                    <span data-testid="wallet-address">{truncate(address)}</span>
                )}
                <ChevronDown size={12} className="text-mute" />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="wallet-panel"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.25 }}
                        className={panelCls(align)}
                    >
                        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-[#31D8FF]">WALLET</p>
                        <div className="mt-3 space-y-2.5">
                            <div className="flex items-center justify-between gap-4">
                                <span className="label-tech">ADDRESS</span>
                                <span data-testid="wallet-panel-address" className="font-mono text-xs text-ink">{truncate(address)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-4">
                                <span className="label-tech">NETWORK</span>
                                {isWrongNetwork ? (
                                    <span data-testid="wallet-wrong-network" className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#FF5C7A]">
                                        WRONG NETWORK — CHAIN {chainId}
                                    </span>
                                ) : (
                                    <span data-testid="wallet-network-label" className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#31D8FF]">
                                        ARC MAINNET · 5042
                                    </span>
                                )}
                            </div>
                            {!isWrongNetwork && (
                                <>
                                    <div className="flex items-center justify-between gap-4">
                                        <span className="label-tech">USDC BALANCE</span>
                                        <span data-testid="wallet-usdc-balance" className="font-mono text-xs tabular-nums text-ink">
                                            {balance === null ? "…" : `${balance} USDC`}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-4">
                                        <span className="label-tech">$ACTRA BALANCE</span>
                                        <span data-testid="wallet-actra-balance" className="font-mono text-xs tabular-nums text-[#31D8FF]">
                                            {actraBalance === null ? "…" : actraBalance}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                        {isWrongNetwork && (
                            <div className="mt-4">
                                <button
                                    data-testid="wallet-switch-arc"
                                    onClick={() => {
                                        setSwitchError(null);
                                        switchToArc().catch((e) => setSwitchError(e?.message || "SWITCH FAILED"));
                                    }}
                                    className="w-full bg-[#1677FF] px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#3D8BFF]"
                                >
                                    SWITCH TO ARC
                                </button>
                                {switchError && (
                                    <p data-testid="wallet-switch-error" className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#FF5C7A]">
                                        <AlertTriangle size={11} /> {switchError}
                                    </p>
                                )}
                            </div>
                        )}
                        <button
                            data-testid="wallet-disconnect"
                            onClick={() => {
                                setOpen(false);
                                disconnectWallet();
                            }}
                            className="mt-4 flex w-full items-center justify-center gap-2 border border-white/15 px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-mute transition-colors hover:text-ink"
                        >
                            <LogOut size={12} /> DISCONNECT
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default WalletButton;
